import React from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import LinkSelector from './LinkSelector';
import ColorPickerSwatch from './ColorPickerSwatch';
import {
  BUTTON_ICON_OPTIONS, renderButtonIcon, getButtonVariantStyle,
  getButtonSizeClass, getButtonHoverClass,
} from './buttonBuilderUtils';
import ButtonPresetsBar from './ButtonPresetsBar';
import { pickNested } from './buttonPresets';

// Reusable Button Editor Component for any block
const BlockButtonEditor = ({ content, onChange, label = "Add Button", pages = [], anchors = [] }) => {
  const hasButton = content.blockButton?.enabled;
  const btn = content.blockButton || {};
  const setBtn = (patch) => onChange({ ...content, blockButton: { ...content.blockButton, ...patch } });

  return (
    <div className="mt-3 pt-3 border-t border-slate-200">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={hasButton || false}
          onChange={(e) => onChange({ 
            ...content, 
            blockButton: { 
              ...content.blockButton, 
              enabled: e.target.checked,
              text: content.blockButton?.text || 'Click Me',
              url: content.blockButton?.url || '',
              style: content.blockButton?.style || 'solid',
              size: content.blockButton?.size || 'md',
              color: content.blockButton?.color || '#6366f1',
              textColor: content.blockButton?.textColor || '#ffffff',
              gradientColor: content.blockButton?.gradientColor || '#8b5cf6',
              radius: content.blockButton?.radius || 'rounded',
              hover: content.blockButton?.hover || 'fade',
              icon: content.blockButton?.icon || 'none',
              iconPosition: content.blockButton?.iconPosition || 'right',
              align: content.blockButton?.align || 'center',
              fullWidth: content.blockButton?.fullWidth || false,
              marginTop: content.blockButton?.marginTop || 16,
            }
          })}
          className="w-4 h-4 rounded border-slate-300 accent-indigo-600 cursor-pointer"
        />
        <span className="text-xs font-medium text-slate-600">{label}</span>
      </label>
      
      {hasButton && (
        <div className="mt-2 space-y-2 p-2 bg-slate-50 rounded-lg">
          {/* Presets bar */}
          <ButtonPresetsBar
            currentStyle={pickNested(btn)}
            onApply={(nested) => setBtn(nested)}
          />
          <Input
            value={content.blockButton?.text || 'Click Me'}
            onChange={(e) => onChange({ 
              ...content, 
              blockButton: { ...content.blockButton, text: e.target.value }
            })}
            placeholder="Button Text"
            className="h-8 text-sm"
          />
          <LinkSelector
            value={content.blockButton?.url || ''}
            onChange={(url) => onChange({ 
              ...content, 
              blockButton: { ...content.blockButton, url }
            })}
            pages={pages}
            anchors={anchors}
            label="Button URL"
            testId="block-button-url"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={content.blockButton?.style || 'solid'}
              onValueChange={(value) => onChange({ 
                ...content, 
                blockButton: { ...content.blockButton, style: value }
              })}
            >
              <SelectTrigger className="w-20 h-7 text-xs" data-testid="block-button-style">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="outline">Outline</SelectItem>
                <SelectItem value="ghost">Ghost</SelectItem>
                <SelectItem value="gradient">Gradient</SelectItem>
                <SelectItem value="soft">Soft</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={content.blockButton?.size || 'md'}
              onValueChange={(value) => onChange({ 
                ...content, 
                blockButton: { ...content.blockButton, size: value }
              })}
            >
              <SelectTrigger className="w-16 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Sm</SelectItem>
                <SelectItem value="md">Md</SelectItem>
                <SelectItem value="lg">Lg</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">BG:</label>
              <ColorPickerSwatch
                value={content.blockButton?.color || '#6366f1'}
                onChange={(e) => onChange({ 
                  ...content, 
                  blockButton: { ...content.blockButton, color: e.target.value }
                })}
                data-testid="block-button-bg-color"
              />
            </div>
            
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Text:</label>
              <ColorPickerSwatch
                value={content.blockButton?.textColor || '#ffffff'}
                onChange={(e) => onChange({ 
                  ...content, 
                  blockButton: { ...content.blockButton, textColor: e.target.value }
                })}
                data-testid="block-button-text-color"
              />
            </div>

            {btn.style === 'gradient' && (
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-500">Gradient:</label>
                <ColorPickerSwatch
                  data-testid="block-button-gradient-color"
                  value={content.blockButton?.gradientColor || '#8b5cf6'}
                  onChange={(e) => setBtn({ gradientColor: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* Corner radius · Hover · Icon */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Corners:</label>
              <Select
                value={content.blockButton?.radius || 'rounded'}
                onValueChange={(value) => setBtn({ radius: value })}
              >
                <SelectTrigger className="w-24 h-7 text-xs" data-testid="block-button-radius">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="square">Square</SelectItem>
                  <SelectItem value="rounded">Rounded</SelectItem>
                  <SelectItem value="pill">Pill</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Hover:</label>
              <Select
                value={content.blockButton?.hover || 'fade'}
                onValueChange={(value) => setBtn({ hover: value })}
              >
                <SelectTrigger className="w-24 h-7 text-xs" data-testid="block-button-hover">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fade">Fade</SelectItem>
                  <SelectItem value="darken">Darken</SelectItem>
                  <SelectItem value="lift">Lift</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Icon */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Icon:</label>
              <Select
                value={content.blockButton?.icon || 'none'}
                onValueChange={(value) => setBtn({ icon: value })}
              >
                <SelectTrigger className="w-28 h-7 text-xs" data-testid="block-button-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUTTON_ICON_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {btn.icon && btn.icon !== 'none' && (
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-500">Position:</label>
                <Select
                  value={content.blockButton?.iconPosition || 'right'}
                  onValueChange={(value) => setBtn({ iconPosition: value })}
                >
                  <SelectTrigger className="w-20 h-7 text-xs" data-testid="block-button-icon-position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {btn.icon && btn.icon !== 'none' && (
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-500">Icon color:</label>
                <ColorPickerSwatch
                  data-testid="block-button-icon-color"
                  value={content.blockButton?.iconColor || content.blockButton?.textColor || '#ffffff'}
                  onChange={(e) => setBtn({ iconColor: e.target.value })}
                />
                {content.blockButton?.iconColor && (
                  <button type="button" onClick={() => setBtn({ iconColor: undefined })} className="text-[10px] text-slate-400 hover:text-slate-600" title="Match text color">reset</button>
                )}
              </div>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={content.blockButton?.align || 'center'}
              onValueChange={(value) => onChange({ 
                ...content, 
                blockButton: { ...content.blockButton, align: value }
              })}
            >
              <SelectTrigger className="w-20 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={content.blockButton?.verticalAlign || 'bottom'}
              onValueChange={(value) => onChange({ 
                ...content, 
                blockButton: { ...content.blockButton, verticalAlign: value }
              })}
            >
              <SelectTrigger className="w-20 h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="top">Top</SelectItem>
                <SelectItem value="middle">Middle</SelectItem>
                <SelectItem value="bottom">Bottom</SelectItem>
              </SelectContent>
            </Select>
            
            <label className="flex items-center gap-1 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={content.blockButton?.fullWidth || false}
                onChange={(e) => onChange({ 
                  ...content, 
                  blockButton: { ...content.blockButton, fullWidth: e.target.checked }
                })}
                className="w-3 h-3 rounded accent-indigo-600 cursor-pointer"
              />
              Full Width
            </label>

            <label className="flex items-center gap-1 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={content.blockButton?.openInNewTab || false}
                onChange={(e) => onChange({ 
                  ...content, 
                  blockButton: { ...content.blockButton, openInNewTab: e.target.checked }
                })}
                className="w-3 h-3 rounded accent-indigo-600 cursor-pointer"
                data-testid="block-button-new-tab"
              />
              New window
            </label>
            
            <div className="flex items-center gap-1">
              <label className="text-xs text-slate-500">Top:</label>
              <Input
                type="number"
                value={content.blockButton?.marginTop || 16}
                onChange={(e) => onChange({ 
                  ...content, 
                  blockButton: { ...content.blockButton, marginTop: parseInt(e.target.value) || 0 }
                })}
                className="w-12 h-7 text-xs"
                min="0"
                max="100"
              />
            </div>
          </div>
          
          {/* Button Preview */}
          <div className="mt-2 p-2 bg-white rounded text-center">
            <div style={{ textAlign: content.blockButton?.align || 'center' }}>
              <button
                data-testid="block-button-preview"
                className={`inline-flex items-center justify-center gap-2 ${getButtonSizeClass(btn.size)} ${btn.fullWidth ? 'w-full' : ''} font-medium transition-all ${getButtonHoverClass(btn.hover)}`}
                style={getButtonVariantStyle(btn)}
              >
                {btn.icon && btn.icon !== 'none' && (btn.iconPosition || 'right') === 'left' && renderButtonIcon(btn.icon, btn.size === 'lg' ? 20 : 16, btn.iconColor)}
                {btn.text || 'Click Me'}
                {btn.icon && btn.icon !== 'none' && (btn.iconPosition || 'right') === 'right' && renderButtonIcon(btn.icon, btn.size === 'lg' ? 20 : 16, btn.iconColor)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockButtonEditor;
